import {
  PRODUCTS,
  OP_EQUALS,
  TEST_TYPE,
  TYPE_OPTION,
  DEVICE_TYPE,
  TEST_STATUS,
  SOURCE_FIGMA,
  SURVEY_TYPES,
  SOURCE_MARVEL,
  SOURCE_INVISION,
  QUESTION_CATEGORY_RADIO,
  QUESTION_CATEGORY_CHECKBOX,
  TEST_TYPE_TASK,
  TEST_TYPE_NORECORDING,
  TOOLKIT,
  TEST_TYPE_CARDSORT,
  TEST_TYPE_TREETEST,
  TEST_TYPE_MODERATED,
} from "../actions/constants";
import { PATHS } from "./paths";
import dayjs from "dayjs";
import advancedFormat from "dayjs/plugin/advancedFormat";

// Enable the plugin
dayjs.extend(advancedFormat);

const { RADIO, CHECKBOX } = SURVEY_TYPES;
const { REACT_APP_BASE_URL, REACT_APP_BASENAME } = process.env;

// Get Yii URL
export const getYiiURL = (path = "") => `${REACT_APP_BASE_URL}/${path}`;

// Get S3 URL
export const getS3URL = (path = "") => `${process.env.REACT_APP_S3_BASEPATH}/${path}`;

// Get Frontend URL
export const getFrontendURL = (path = "") => `${window.location.origin + REACT_APP_BASENAME}/${path}`;

export const getPercent = (value = 0, total = 0, toFix = false) => {
  if (total <= 0) return 0;

  const percent = (value / total) * 100;
  return toFix ? percent.toFixed(2) : Math.round(percent);
};

export const getRandomID = (size = 12) => {
  const charset = "abcdefghijklmnopqrstuvwxyz";
  return [...Array(size)].map((_) => charset[Math.floor(Math.random() * charset.length)]).join("");
};

export const getAnswerObj = (value = "") => ({ op: OP_EQUALS, var: [{ type: TYPE_OPTION, value }] });

export const getOption = (position = 0, title = "", type = QUESTION_CATEGORY_RADIO, source_title = "") => ({
  position,
  title,
  source_title,
  attachment: [],
  properties: {},
  id: getRandomID(),
  criteria: type === QUESTION_CATEGORY_CHECKBOX ? "maybe" : "must",
});

export const getQuestion = (position = 0) => ({
  position,
  title: "",
  source_title: "",
  validation: {},
  attachment: [],
  id: getRandomID(),
  options: [getOption()],
  answers: getAnswerObj(),
  category: QUESTION_CATEGORY_RADIO,
  translation: { tool: null, lang: null },
  properties: { optional: false, use_others: false },
});

export const getParam = (location, key) => new URLSearchParams(location.search).get(key);

// Convert \n into <br>
export const newLineToBr = (string = "") => string.replace(/\n/g, "<br />");

export const goBackToWebsite = (referrer) =>
  window.location.assign(referrer ? referrer : `${process.env.REACT_APP_SERVER_LOGIN_URL}?returnUrl=${window.location.href}`);

export const getDeviceName = (deviceType) => {
  const deviceNames = {
    [DEVICE_TYPE.TABLET]: "Tablet",
    [DEVICE_TYPE.MOBILE]: "Smartphone",
    [DEVICE_TYPE.COMPUTER]: "Computer (PC)",
    [DEVICE_TYPE.TABLET_IPAD]: "Tablet / iPad",
    [DEVICE_TYPE.TABLET_AND_MOBILE]: "Mobile / Tablet",
  };

  return deviceNames[deviceType] || "Unknown";
};

export const getTestStatus = (status) => {
  const testStatus = {
    [TEST_STATUS.DRAFT]: "Not Activated ",
    [TEST_STATUS.INACTIVE]: "Inactive",
    [TEST_STATUS.ACTIVE]: "Active",
    [TEST_STATUS.COMPLETED]: "Completed",
    [TEST_STATUS.HIDDEN]: "Hidden",
    [TEST_STATUS.DRYRUN]: "Dry Run Mode",
    [TEST_STATUS.TEMP_AB_TEST]: "Dropout",
    [TEST_STATUS.SAVED_TEST]: "Draft",
    [TEST_STATUS.DELETE_SUFFIX]: "Deleted",
    [TEST_STATUS.EDIT_TEST]: "Draft",
    [TEST_STATUS.PREVIEW_TEST]: "Preview",
  };

  return testStatus[status] || "Unknown";
};

export const getTestTyeName = (testType) => {
  const testTypeNames = {
    [TEST_TYPE.MULTIOS_APP]: "iOS/Android App Test",
    [TEST_TYPE.IOS_APP]: "iOS App Test",
    [TEST_TYPE.IOS_SDK]: "iOS SDK Test",
    [TEST_TYPE.WEBSITE]: "Website Test",
    [TEST_TYPE.MODERATED]: "Moderated Test",
    [TEST_TYPE.ANDROID_APP]: "Android App Test",
    [TEST_TYPE.ANDROID_SDK]: "Android SDK Test",
  };

  return testTypeNames[testType] || "Unknown";
};

export const getTestTypeForRecruit = (test, isToolkit = false, isModerated = false) => {
  if (isModerated) {
    return TEST_TYPE_MODERATED;
  } else if (isToolkit) {
    // Toolkit
    if (test?.data?.type === TOOLKIT.TEST_TYPES.cardsort || test?.type === TOOLKIT.TEST_TYPES.cardsort) {
      // Card sort
      return TEST_TYPE_CARDSORT;
    }
    if (test?.data?.type === TOOLKIT.TEST_TYPES.treetest || test?.type === TOOLKIT.TEST_TYPES.treetest) {
      // Tree test
      return TEST_TYPE_TREETEST;
    }
  } else {
    // TBT, Figma no recording, Survey, Moderated
    if (test?.type === TEST_TYPE_TASK && Boolean(Number(test?.is_recording_off))) {
      // Figma no recording test
      return TEST_TYPE_NORECORDING;
    }
    return test?.type;
  }
};

export const isPanelistCountValid = (responsesCollected, users_max, uxarmyCredits, creditsPanel) => {
  const uc = (Number(users_max) - Number(responsesCollected?.panelTesters || 0)) * Number(uxarmyCredits);
  return uc > Number(creditsPanel) ? false : true;
};

export const uxTestersNationalityStringToArray = (ux_testers_nationality) => {
  return ux_testers_nationality
    ? ux_testers_nationality.toString().replaceAll('"', "").replaceAll("'", "").replace(/[[\]]/g, "").split(",")
    : null;
};

export const uxTestersNationalityArrayToString = (ux_testers_nationality) => {
  return ux_testers_nationality ? "[" + ux_testers_nationality.map((i) => `"${i}"`).join() + "]" : null;
};

export const allowedExternalPrototypesRegex = [
  /^https:\/\/(?:[\w.-]+\.)?figma\.com\/(?:file|proto)\/([0-9a-zA-Z]{22,128})(?:\/.*)?$/,
  /^https:\/\/(\[\w\.-]+\.)?marvelapp\.com\/prototype\/([0-9a-zA-Z]+)\/?.*$/,
  /^https:\/\/([\w.-]+.)?invis\.io\/([0-9a-zA-Z]+)\/?.*$/,
  /^https:\/\/([\w.-]+.)?invisionapp\.com\/([0-9a-zA-Z]+)\/?.*$/,
];

export const isAllowedExternalPrototype = (url) => {
  let isRegexMatched = false;
  allowedExternalPrototypesRegex.forEach((r) => {
    if (url.match(r)) {
      isRegexMatched = true;
    }
  });
  return isRegexMatched;
};

export const getPrototypeIcon = (prototypeSource) => {
  let prototypeIcon = "";
  switch (Number(prototypeSource)) {
    case SOURCE_INVISION:
      prototypeIcon = "proto_invision";
      break;
    case SOURCE_FIGMA:
      prototypeIcon = "proto_figma";
      break;
    case SOURCE_MARVEL:
      prototypeIcon = "proto_marvel";
      break;
    default:
      prototypeIcon = "";
      break;
  }

  return prototypeIcon;
};

export function getPrototypeSource(url) {
  let source = null;
  if (url.includes("invis.io") || url.includes("invisionapp.com")) {
    source = SOURCE_INVISION;
  } else if (url.includes("marvelapp.com")) {
    source = SOURCE_MARVEL;
  } else if (url.includes("figma.com")) {
    source = SOURCE_FIGMA;
  }
  return source;
}

export const getPrototypeIframeSrc = (url, source = null, isPreview = false, isDesktop = true, continueURL = false, nodeId) => {
  if (!url) return "";

  const PREVIEW_SCALING_FACTOR = "scale-down-width";
  const CREATION_SCALING_FACTOR = "scale-down";

  let iframeSrc = "";

  // ------ For external prototypes which are importable via UI
  if (source === SOURCE_FIGMA) {
    // Figma
    const scalingStrings = ["fit-width", "min-zoom", "max-zoom", "scale-down-width", "scale-down", "contain"];
    scalingStrings.forEach((scale) => {
      url = url.replace(scale, "");
    });
    const scalingFactor = isPreview ? PREVIEW_SCALING_FACTOR : CREATION_SCALING_FACTOR;

    iframeSrc = `${getFigmaEmbedUrl(url, scalingFactor, continueURL, nodeId)}&bg-color=ffffff${!isDesktop && "&device-frame=false"}`;
  } else if (source === SOURCE_MARVEL) {
    // Marvel
    const regex1 = /^https:\/\/(\[\w\.-]+\.)?marvelapp\.com\/prototype\/([0-9a-zA-Z]+)\/?$/;
    const regex2 = /^https:\/\/(\[\w\.-]+\.)?marvelapp\.com\/prototype\/([0-9a-zA-Z]+)\/screen\/([0-9a-zA-Z]+)\/?$/;
    if (url.match(regex1) || url.match(regex2)) {
      iframeSrc = url + "?emb=1&iosapp=false&frameless=false";
    } else {
      const splitUrl = url.split("screen");
      if (splitUrl.length > 1) {
        url = splitUrl[0].replace(/\/$/, "") + "/screen/" + splitUrl[1].replace(/\//, "");
      } else {
        url = splitUrl[0].replace(/\/$/, "");
      }
    }

    iframeSrc = url + "?emb=1&iosapp=false&frameless=false";
  } else if (source === SOURCE_INVISION) {
    // Invision
    iframeSrc = url;
  } else {
    // ------ For other prototypes which are not importable via UI
    let matches = null;
    const xdRegex = /^https:\/\/xd.adobe.com\/view\/(.*)?\/.*$/;
    if ((matches = url.match(xdRegex)) && matches.length > 1) {
      // Adobe XD
      iframeSrc = "https://xd.adobe.com/embed/" + matches[1] + "/?fullscreen&hints=off";
    }
  }

  return iframeSrc;
};

export const fixExternalPrototypeUrl = (url) => {
  if (getPrototypeSource(url) === SOURCE_MARVEL) {
    url = url.replace(/(\/screen\/[^/]+)(?!.*\1)/, ""); // for removing screen params from url in case of marvel
  }
  return url;
};

export const getExternalPrototypeURL = (nodeId, prototype) => {
  if (!nodeId) return null;

  const prototypeSource = prototype?.prototype_source;

  let url = null;

  prototype.url = fixExternalPrototypeUrl(prototype.url);

  switch (Number(prototypeSource)) {
    case SOURCE_FIGMA:
      const s = prototype.url.split("?");
      const protoBaseUrl = s[0];
      const protoBaseParams = s[1];
      if (protoBaseParams !== undefined) {
        const params = protoBaseParams
          .split("&")
          .reduce((r, a) => {
            let b = a.split("=");
            let str = b[0] + "=" + (b[0] === "node-id" ? encodeURIComponent(nodeId) : b[1]);
            return [...r, str];
          }, [])
          .join("&");
        url = `${protoBaseUrl}?${params}`;
      } else {
        url = prototype.url;
      }
      break;
    case SOURCE_INVISION:
      url = `${prototype.url}/#/screens/${nodeId}`;
      break;
    case SOURCE_MARVEL:
      url = `${prototype.url}/screen/${nodeId}`;
      break;
    default:
      url = null;
      break;
  }
  return url;
};

export const getEmbedUrl = (url) => {
  if (url.includes("figma"))
    return `https://figma.com/embed?embed_host=share&url=${encodeURIComponent(url)}%26scaling%3Dmini-zoom%26hide-ui%3D1`;
  else if (url.includes("marvelapp")) return `${url}?emb=1&iosapp=false&frameless=true`;
  else if (url.includes("invisionapp")) return `${url}`;
  else return url;
};

export const extractDomain = (url) => {
  // Find & remove protocol (http, ftp, etc.) and get domain
  let domain = url.split("/")[url.indexOf("://") > -1 ? 2 : 0];

  // Find & remove port number
  domain = domain.split(":")[0];

  // Remove subdomains if any
  const parts = domain.split(".");
  if (parts.length > 2) domain = parts.slice(1).join(".");

  return domain;
};

export const isSameURL = (url1, url2) => {
  const url1Info = new URL(!url1.startsWith("http") ? `http://${url1}` : url1);
  const url2Info = new URL(url2, url1Info.origin);
  return extractDomain(url1Info.href) === extractDomain(url2Info.href);
};

// Reorder the ArrayList
export const reorderArrayList = (list, startIndex, endIndex) => {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);

  return result;
};

// Moves an item from one list to another list.
export const moveItemsBetweenList = (source, destination, droppableSource, droppableDestination) => {
  const sourceClone = Array.from(source);
  const destClone = Array.from(destination);
  const [removed] = sourceClone.splice(droppableSource.index, 1);

  destClone.splice(droppableDestination.index, 0, removed);

  const result = {};
  result[droppableSource.droppableId] = sourceClone;
  result[droppableDestination.droppableId] = destClone;

  return result;
};

// Function to remove HTML tags and keep line breaks intact - as quiiljs does
export const getTextLengthAsQuillJS = (input) => {
  if (!input) return 0;
  // Replace line breaks inside <p> and <br> tags with a special token
  const input2 = input?.replace(/(<p><br>)/g, "\n");
  const withTokens = input2.replace(/(<p>|<br>)/g, "\n");

  // Remove other HTML tags by matching everything between '<' and '>'
  const withoutTags = withTokens.replace(/<[^>]*>/g, "");

  // Restore the line breaks from the special token
  const withLineBreaks = withoutTags.replace(/\n/g, "\n");

  const doc = new DOMParser().parseFromString(withLineBreaks, "text/html");
  const textWithoutSpecialEntities = doc.documentElement.textContent;

  // Remove leading and trailing spaces and return the result
  const length = textWithoutSpecialEntities.length;
  return length === 0 ? 0 : length - 1; // -1 to remove the last line break
};

// Regular expression pattern for URL validation
export const isValidURL = (url) => {
  const urlRegex = /^(?:(?:https?|ftp):\/\/)?(?:www\.)?[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}(?:\/[^/\s]*)*$/;
  return urlRegex.test(url);
};

// Regular expression pattern for BLOB file urls
export const isBlobURL = (url) => {
  const blobRegex = /^blob:(http|https):\/\/[^\s]+$/;
  return blobRegex.test(url);
};

// Get Product List
export const getProductList = (plan = {}) => {
  const { MODERATED, UNMODERATED, CARDSORTING, TREETESTING, SURVEYS } = PRODUCTS;

  return [
    {
      hasAccount: true,
      isAsyncFeedbackCollection: true,
      key: UNMODERATED,
      icon: "unmoderated",
      name: "Usability Testing",
      detailLink: "/remote-user-testing",
      plan: plan?.name || "Free Plan",
      productLink: PATHS.V2.TBT,
      btnText: "Go to Dashboard",
      description: "Test user friendliness of your app, website or prototype",
      logos: ["proto_marvel", "proto_figma", "proto_protoio", "proto_sketch", "proto_xd"],
      keyFeatures: [
        "User Test any design prototype - Figma, Invisionapp, or any Design tool",
        "Measure task success based on usability metrics and navigation paths",
        "Conduct comparative A/B testing for design options",
        "Get video of screen interactions with voice feedback",
        "Transcription, SUS Score, task success rate, heatmaps, navigation paths",
        "Integrated online screener",
      ],
      headingText: "Unmoderated Feedback Collection",
    },
    {
      hasAccount: true,
      isAsyncFeedbackCollection: true,
      key: SURVEYS,
      icon: "surveys",
      name: "Surveys",
      plan: plan?.name || "Free Plan",
      productLink: PATHS.V2.SURVEYS,
      btnText: "Go to Dashboard",
      description: "Quick, Targeted User Feedback",
      keyFeatures: [
        "Gain an understanding of your target audience via simple survey questions. Stay tuned as we bring you more types of survey questions.",
      ],
      headingText: "Unmoderated Feedback Collection",
    },
    {
      hasAccount: true,
      isUserInterview: true,
      key: MODERATED,
      icon: "moderated",
      name: "Moderated research on DeepDive®",
      detailLink: "/moderated-research",
      plan: plan?.name || "Free Plan",
      productLink: PATHS.V2.MODERATED,
      btnText: "Go to Dashboard",
      description: "User Interviews And Focus Groups",
      keyFeatures: [
        "Live user interviews with recording and screen-sharing",
        "Speech-to-text transcription with 15 language options",
        "Dedicated roles of Moderator, Participant, Note-takers,Co-facilitator, Observers, Translator",
        "Auto-consolidated notes that are time-stamped to the recording",
        "Hidden Observers and Note takers with silent group chat invisible to the participant",
        "Tag key findings in each interview for a project level synthesis",
      ],
      headingText: "User Interviews & Focus Groups",
    },
    {
      hasAccount: true,
      isToolkit: true,
      key: CARDSORTING,
      icon: "card_sorting",
      name: "Card Sorting",
      detailLink: "/card-sorting",
      plan: plan?.name || "Free Plan",
      productLink: PATHS.V2.TOOLKIT.CARDSORTING,
      btnText: "Go to Dashboard",
      description: "Optimize Your Information Structure",
      keyFeatures: [
        "Use images along with text labels to create Cards",
        "Add Screener, pre and post questionnaire to your Card sort",
        "Ask feedback and alternative naming for potentially confusing Cards",
        "Conduct Card sorting in the local language",
        "Testers create categories to sort cards in Open card sort",
        "Testers can only sort cards to given categories in Closed cards sort",
      ],
      headingText: "Information Architecture Testing",
    },
    {
      hasAccount: true,
      isToolkit: true,
      key: TREETESTING,
      icon: "tree_testing",
      name: "Tree Testing",
      detailLink: "/tree-testing",
      plan: plan?.name || "Free Plan",
      productLink: PATHS.V2.TOOLKIT.TREETESTING,
      btnText: "Go to Dashboard",
      description: "Validate User Navigation Paths",
      keyFeatures: [
        "Define real-world Tasks and likely results for your Testers",
        "Intuitive visualization of results using data visualisation",
        "Add Screener, pre and post questionnaire to your Tree Test",
        "Download all results in XLS format",
      ],
      headingText: "Information Architecture Testing",
    },
  ];
};

export const canHaveLogic = (task) => {
  const { surveyType } = task || {};
  return surveyType === RADIO || surveyType === CHECKBOX;
};

export const isMobileUserAgent = () => {
  const userAgent = navigator.userAgent;
  const mobileUserAgentPattern =
    /Mobile|iP(hone|od)|Android|BlackBerry|IEMobile|Kindle|NetFront|Silk-Accelerated|(hpw|web)OS|Fennec|Minimo|Opera M(obi|ini)|Blazer|Dolfin|Dolphin|Skyfire|Zune/;
  if (mobileUserAgentPattern.test(userAgent)) return true;
  return false;
};

// Function to get color based on index
export const getColor = (i = 0) => {
  const COLORS = [
    "#2E62E9",
    "#FF499F",
    "#F2C94C",
    "#A871EC",
    "#F2994A",
    "#03A6A6",
    "#EB5757",
    "#BB6BD9",
    "#56CCF2",
    "#6FCF97",
    "#2D9CDB",
    "#9B51E0",
    "#607D8B",
    "#FF7D52",
    "#00ACFF",
    "#2DCA72",
    "#F75FDE",
    "#FFC233",
    "#F53D6B",
  ];

  return COLORS[i % COLORS.length];
};

// Function to convert hex to rgba
export const hexToRgb = (hex, opacity = 1) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? `rgba(${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}, ${opacity})` : null;
};

// Get Country Name
export const getCountryName = (countryCode = "", countries = []) => {
  const country = countries.find((c) => c.iso_alpha2 === countryCode);
  return country?.name || "Unknown";
};

// Get Protype Icon Info
export const getPrototypeIconInfo = (url = "") => {
  // Figma
  if (url.includes("figma")) return { icon: "proto_figma", title: "Figma" };

  // Marvel
  if (url.includes("marvelapp")) return { icon: "proto_marvel", title: "Marvel" };

  // Invision
  if (url.includes("invisionapp") || url?.includes("invis.io")) return { icon: "proto_invision", title: "Invision" };

  // adobe xd
  if (url.includes("xd.adobe.com")) return { icon: "proto_xd", title: "Adobe XD" };

  // proto.io
  if (url.includes("proto.io")) return { icon: "proto_protopie", title: "ProtoPie" };

  // Sketch
  if (url.includes("sketch")) return { icon: "proto_sketch", title: "Sketch" };

  return "";
};

export const getCurrentTime = () => Math.round(new Date().getTime() / 1000);

export const getCustomImageName = (i = 0) => `Image ${String.fromCharCode(i + 65)}`;
export const getSeverityColor = (severity) => {
  switch (severity) {
    case "success":
      return "#28a745";
    case "info":
      return "#17a2b8";
    case "warning":
      return "#fd7e14";
    case "error":
      return "#dc3545";
    case "danger":
      return "#bd2130";
    default:
      return "#008000";
  }
};

export const getFigmaNodeIdFromUrl = (url) => {
  let urlToCheck = url;
  if (!urlToCheck.startsWith("http://www." || "https://www.")) urlToCheck = `http://www.${urlToCheck}`;
  try {
    const urlObj = new URL(urlToCheck);
    let nodeID = urlObj.searchParams.get("node-id")?.replace("-", ":");
    if (!nodeID) nodeID = urlObj.searchParams.get("starting-point-node-id")?.replace("-", ":");
    return nodeID;
  } catch {
    return null;
  }
};

// Replace www.figma with embed.figma
export const getFigmaEmbedUrl = (url, scalingFactor = "scale-down", continueURL, nodeId) => {
  return `${url?.replace("www.figma.com", "embed.figma.com")}&embed-host=uxarmy&client-id=${
    process.env.REACT_APP_FIGMA_CLIENT_ID
  }&footer=false&page-selector=false&viewport-controls=false&disable-default-keyboard-nav=false&hide-ui=1&scaling=${scalingFactor}${
    continueURL ? `&node-id=${nodeId?.replace(":", "-")}` : ""
  }`;
};

// Calculate Figma Path Errors
export const calculatePathErrors = (pathsArr = [], currentPathObj = {}) => {
  const { currentPathIndex = 0, currentPathIds = [] } = currentPathObj || {};

  // convert all paths to array of ids
  const pathsArrIds = pathsArr.map((path) => (JSON.parse(path?.url || "[]") || []).map((url) => getFigmaNodeIdFromUrl(url)));
  const allLastElements = pathsArrIds.map((path, i) => ({ lastElement: path[path.length - 1], pathIndex: i }));

  const errObj = { hasError: false, errorPaths: [] };

  // Validation 1: A path must have atleast two nodes
  if (currentPathIds.length <= 1) {
    errObj.errorPaths.push({
      type: "single-node",
      pathIndex: currentPathIndex,
      message: `This path(Path ${currentPathIndex + 1}) must have atleast two nodes.`,
    });
    errObj.hasError = true;
  } else {
    // Validation 2: A path must not be identical to another path
    pathsArrIds.reduce((obj, pathIds, index) => {
      if (JSON.stringify(pathIds) === JSON.stringify(currentPathIds) && index !== currentPathIndex) {
        obj.hasError = true;
        obj.errorPaths.push({
          type: "identical",
          pathIndex: index,
          conflictingPathIndex: index,
          message: `This path is identical to another path (Path ${index + 1})`,
        });
      }

      return obj;
    }, errObj);

    // Validation 3: The end screen of another path must not come in the middle of this path
    allLastElements?.reduce((obj, pathObj) => {
      // Check if an identical error already exists
      if (obj?.hasError && obj?.errorPaths?.find(({ type, pathIndex }) => type === "identical" && pathIndex === pathObj?.pathIndex)) {
        return obj;
      }

      const conflictingScreenIndex = currentPathIds?.slice(0, -1)?.indexOf(pathObj?.lastElement);
      if (conflictingScreenIndex > -1) {
        obj.hasError = true;
        obj.errorPaths.push({
          type: "conflict",
          pathIndex: pathObj?.pathIndex,
          conflictingPathIndex: pathObj?.pathIndex,
          conflictStartIndex: conflictingScreenIndex,
          message: `The end screen of another path (Path ${pathObj.pathIndex + 1}) must not come in the middle of this path (Screen ${
            conflictingScreenIndex + 1
          })`,
        });
      }

      return errObj;
    }, errObj);
  }

  return errObj;
};

// Update iframs frame to the given node id
export const changeFigmaiFrameNodeId = (iframeRef = null, nodeId = "") => {
  if (!iframeRef) return;

  // Post message to iframe
  iframeRef?.contentWindow?.postMessage({ data: { nodeId }, type: "NAVIGATE_TO_FRAME_AND_CLOSE_OVERLAYS" }, "https://www.figma.com");
};

export const resourcesLinks = [
  // { key: 1, label: "Sample Figma prototype", link: "/" },
  // { key: 2, label: "Sample images", link: "/" },
  { key: 3, label: "Knowledge base", link: "https://help.uxarmy.com/ " },
  {
    key: 4,
    label: "How UXArmy's credit system works",
    link: "https://help.uxarmy.com/space/general/196902918/How+UXArmy's+Credit+System+Works ",
  },
  {
    key: 5,
    label: "Recruiting UXArmy's user panel",
    link: "https://help.uxarmy.com/space/UT/91226154/Overview+of+Inviting+Participants ",
  },
  // { key: 6, label: "Need help with research", link: "https://www.uxarmy.com/contact-sales" },
];

export const getFormattedNumber = (number) => new Intl.NumberFormat()?.format(number);

// Get Formatted Date
export const getFormattedDayjsDate = (date, format = "MMM D, YYYY") => dayjs(date).format(format);

export const roundCredits = (credits, prec = 2) => {
  return parseFloat(Number(credits).toFixed(prec));
};

export const scaleHeatmapDataPoints = (points, originalWidth, originalHeight, renderedWidth, renderedHeight) => {
  const scaleX = renderedWidth / originalWidth;
  const scaleY = renderedHeight / originalHeight;

  return points.map((p) => {
    return {
      ...p,
      x: parseInt(p.x * scaleX),
      y: parseInt(p.y * scaleY),
    };
  });
};

// Is Invalid emails
export const isInvalidEmail = (email) => email && !email.match(/^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,32})+$/);

// Emails Has Errors
export const emailsHasErrors = (emails = [], disallowGmail = false) => {
  if (!emails || emails.length === 0) return { error: true, errMessage: "Please fill the emails" };

  if (emails.some(isInvalidEmail)) return { error: true, errMessage: "Please remove the invalid emails." };

  // Do not allow gmail emails
  if (disallowGmail && emails.some((email) => email.includes("@gmail.com"))) {
    return { error: true, errMessage: "Please use a non-Gmail account to proceed." };
  }

  return { error: false, errMessage: "" };
};

export const normalizeUrl = (url) => {
  if (url) {
    const httpRegex = /^https?:\/\//;
    const wwwOnlyRegex = /^www\./;

    // Remove https:// or http:// if present
    url = url.replace(httpRegex, "");

    // Remove www if it's the only subdomain
    if (wwwOnlyRegex.test(url)) {
      url = url.replace(wwwOnlyRegex, "");
    }
  }
  return url;
};
